import React, { useState, useContext, useEffect } from "react";
import {
  FaSearch,
  FaUser,
  FaEnvelope,
  FaCalendar,
  FaComment,
  FaBan,
  FaCheck,
  FaEllipsisV,
} from "react-icons/fa";
import DataTable from "react-data-table-component";
import { AppContext } from "../../Context/Provider";
import CommentActionModal from "../../Components/CommentActionModal";

function CommentsManagement() {
  const [filterText, setFilterText] = useState("");
  const [selectedComment, setSelectedComment] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const { FetchCommentsData, CommentsData, UpdateCommentStatus } =
    useContext(AppContext);

  // Fetch comments data on component mount
  useEffect(() => {
    FetchCommentsData();
  }, []);

  // Comments data from API
  const commentsData = CommentsData || [];

  // Filter data based on search text
  const filteredData = commentsData.filter(
    (item) =>
      (item.name || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.email || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.comments || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.status || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.date || "").toLowerCase().includes(filterText.toLowerCase())
  );

  // Handle comment row click
  const handleCommentClick = (row) => {
    setSelectedComment(row);
    setShowActionModal(true);
  };

  // Handle status update from modal
  const handleStatusUpdate = (status) => {
    if (!selectedComment) return;
    UpdateCommentStatus(selectedComment._id, status);
    setShowActionModal(false);
    setSelectedComment(null);
  };

  // Close modal
  const closeModal = () => {
    setShowActionModal(false);
    setSelectedComment(null);
  };

  // Comments table columns
  const columns = [
    {
      name: "NAME",
      selector: (row) => row.name || "N/A",
      sortable: true,
      grow: 1,
      minWidth: "150px",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <FaUser className="text-gray-500 text-sm" />
          <span className="font-medium">{row.name || "N/A"}</span>
        </div>
      ),
    },
    {
      name: "EMAIL",
      selector: (row) => row.email || "N/A",
      sortable: true,
      grow: 2,
      minWidth: "200px",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <FaEnvelope className="text-gray-500 text-sm" />
          <span className="text-blue-600 hover:text-blue-800">
            {row.email || "N/A"}
          </span>
        </div>
      ),
    },
    {
      name: "COMMENT",
      selector: (row) => row.comments || "N/A",
      sortable: true,
      grow: 3,
      minWidth: "300px",
      cell: (row) => (
        <div className="flex items-start gap-2">
          <FaComment className="text-gray-500 text-sm mt-1 flex-shrink-0" />
          <span className="text-sm leading-relaxed">
            {row.comments || "N/A"}
          </span>
        </div>
      ),
    },
    {
      name: "STATUS",
      selector: (row) => row.status || "N/A",
      sortable: true,
      grow: 1,
      minWidth: "120px",
      cell: (row) => {
        const status = (row.status || "").toLowerCase();
        let icon, bgColor;

        switch (status) {
          case "blocked":
            icon = <FaBan className="text-red-500 text-sm" />;
            bgColor = "bg-red-100 text-red-800";
            break;
          case "deleted":
            icon = <FaBan className="text-gray-500 text-sm" />;
            bgColor = "bg-gray-100 text-gray-800";
            break;
          case "approved":
            icon = <FaCheck className="text-green-500 text-sm" />;
            bgColor = "bg-green-100 text-green-800";
            break;
          case "pending":
          default:
            icon = <FaCalendar className="text-yellow-500 text-sm" />;
            bgColor = "bg-yellow-100 text-yellow-800";
            break;
        }

        return (
          <div className="flex items-center gap-2">
            {icon}
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor}`}
            >
              {row.status || "N/A"}
            </span>
          </div>
        );
      },
    },
    {
      name: "DATE",
      selector: (row) => row.date || row.createdAt || "N/A",
      sortable: true,
      grow: 1,
      minWidth: "150px",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <FaCalendar className="text-gray-500 text-sm" />
          <span className="text-sm">
            {row.date || row.createdAt
              ? new Date(row.date || row.createdAt).toLocaleString()
              : "N/A"}
          </span>
        </div>
      ),
    },
    {
      name: "ACTIONS",
      selector: (row) => row._id,
      sortable: false,
      grow: 0,
      width: "120px",
      cell: (row) => {
        return (
          <div className="relative">
            <button
              onClick={() => handleCommentClick(row)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FaEllipsisV className="text-sm" />
            </button>
          </div>
        );
      },
    },
  ];

  // Custom styles for DataTable
  const customStyles = {
    rows: {
      style: {
        minHeight: "72px",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "#f3f4f6",
        },
      },
    },
    headCells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
        backgroundColor: "#f8f9fa",
        fontWeight: "bold",
      },
    },
    cells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
      },
    },
  };

  // Search component for subheader
  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <div className="flex flex-col items-end space-y-2 w-full p-4">
        <input
          type="text"
          onChange={(e) => setFilterText(e.target.value)}
          value={filterText}
          placeholder="Search comments..."
          className="border rounded p-2 w-full max-w-xs"
        />
      </div>
    );
  }, [filterText, resetPaginationToggle]);

  return (
    <div className="px-8 py-2">
      <DataTable
        columns={columns}
        data={filteredData}
        pagination
        paginationResetDefaultPage={resetPaginationToggle}
        subHeader
        subHeaderComponent={subHeaderComponentMemo}
        persistTableHead
        customStyles={customStyles}
        className="shadow-lg bg-white rounded"
        defaultSortField="date"
        defaultSortAsc={false}
        responsive={true}
        fixedHeader
        highlightOnHover
      />

      {/* Action Modal */}
      {showActionModal && selectedComment && (
        <CommentActionModal
          isOpen={showActionModal}
          comment={selectedComment}
          onClose={closeModal}
          onStatusUpdate={handleStatusUpdate}
        />
      )}
    </div>
  );
}

export default CommentsManagement;
